import { IgGreenPrimitives } from "@intelligems/ig-design-tokens/constants";
import { ComponentType } from "@intelligems/ig-types/schemas/visual-builder/mod-schemas";
import {
  EmbeddedHighlight,
  EmbeddedNodeType,
} from "@intelligems/ig-types/schemas/plugin/plugin/embedded";
import { createSelector } from "@reduxjs/toolkit";
import { getHighlightMod } from "@src/store/slices/experienceBuilder/common/selectors/getHighlightMod";
import { getVariationDraft } from "@src/store/slices/experienceBuilder/common/selectors/getVariationDraft";
import { modSelector } from "@src/store/slices/experienceBuilder/common/utils/modSelector";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { EmbeddedMod } from "@src/store/slices/experienceBuilder/types";

const COMPONENT_TYPE_TO_NODE_TYPE: Record<ComponentType, EmbeddedNodeType> = {
  textComponent: "text",
  imageComponent: "image",
  bannerComponent: "layout",
  genericComponent: "layout",
  onsiteUpsell: "layout",
  bundleOnsiteUpsell: "layout",
};

function getHighlightTypes(mod: EmbeddedMod): EmbeddedNodeType[] {
  if ("nodes" in mod) {
    return mod.nodes.map((n) => n.type);
  }
  if (mod.category === "component") {
    return [COMPONENT_TYPE_TO_NODE_TYPE[mod.type]];
  }
  return [];
}

export const getHighlights = createSelector(
  getVariationDraft,
  getHighlightMod,
  experienceBuilderSlice.selectors.getFocusModId,
  (draftMod, highlightMod, focusModId) => {
    const mods: Record<string, EmbeddedHighlight> = {};
    // An anchored mod has no selector to outline in the preview — its theme
    // block owns the position, and may not even be on the previewed page.
    const draftSelector = draftMod ? modSelector(draftMod) : undefined;
    if (draftMod && draftSelector) {
      mods[draftMod.id] = {
        id: draftMod.id,
        selector: draftSelector,
        priority: 50,
        color: IgGreenPrimitives["A100"],
        focus: true,
        hasLabel: false,
        types: getHighlightTypes(draftMod),
        createdAtUrl: draftMod.createdAtUrl,
      };
    }
    const highlightSelector = highlightMod
      ? modSelector(highlightMod)
      : undefined;
    if (highlightMod && highlightSelector) {
      mods[highlightMod.id] = {
        id: highlightMod.id,
        selector: highlightSelector,
        priority: 50,
        types: getHighlightTypes(highlightMod),
        focus: focusModId === highlightMod.id,
      };
    }
    return Object.values(mods);
  }
);
